import {
  showPartnerCheckoutModal,
  pollPartnerCheckoutReturn,
  notifyPartnerStatusChanged,
} from '@/utils/partnerMembership.js';
import { cancelHighlightCheckout } from '@/api/partner';
import { modalController } from '@ionic/vue';
import { installIosPwaLayoutRepair } from '@/utils/iosPwaLayoutRepair';
import { markPartnerMembershipModalReopen, peekPendingPartnerMembershipModal, PARTNER_PROFILE_ROUTE, syncPartnerMembershipCheckoutFromLocation, stashPendingPartnerMembershipModal, isPartnerMembershipCheckoutStarted, consumePartnerMembershipCheckoutStarted } from '@/utils/partnerMembershipNavigation';
import { readStripeEmbeddedCheckoutContext } from '@/utils/stripeEmbeddedCheckout';
import store from '@/store';

const HANDLED_KEY_PREFIX = 'partner_checkout_handled_';
const STASH_KEY = 'partner_checkout_return_stash';
const RETURN_ROUTE_KEY = 'partner:returnRoute';
const STRIPE_CHECKOUT_KEY = 'partner:stripeCheckout';
const STRIPE_CHECKOUT_KIND_KEY = 'partner:stripeCheckoutKind';
export const COMMISSION_INVOICE_CHECKOUT_TYPE = 'commission_invoice';

export const DEFAULT_PARTNER_RETURN = { name: 'navigation.cockpit' };
export const DEFAULT_HIGHLIGHT_CHECKOUT_RETURN = { name: 'navigation.specials' };

/** One in-flight handler per Stripe session (or checkout action) to avoid duplicate modals. */
const inFlightByKey = new Map();
let backGuardTeardown = null;
let membershipModalBackGuardTeardown = null;

export const PARTNER_CHECKOUT_ROUTE_NAMES = new Set([
  'navigation.membership',
  'navigation.highlights',
  'navigation.partner',
  'navigation.werbebudget',
  'navigation.specials',
]);

const PARTNER_CHECKOUT_PATH_RE = /\/navigation\/(membership|highlights|partner|werbebudget|specials)/;
const PROVISION_INVOICE_PATH_RE = /\/navigation\/provision\/invoice\/[^/]+/;

const isProvisionInvoiceRoute = (route) => (
  route?.name === 'navigation.provision-invoice'
  || PROVISION_INVOICE_PATH_RE.test(String(route?.path || route?.fullPath || ''))
);

export const isCommissionInvoiceCheckoutReturn = (routerOrRoute) => {
  const router = routerOrRoute?.currentRoute
    ? routerOrRoute
    : { currentRoute: { value: routerOrRoute } };
  const route = router.currentRoute?.value || routerOrRoute;
  const stash = readStash();
  if (stash?.checkoutType === COMMISSION_INVOICE_CHECKOUT_TYPE) return true;
  if (isProvisionInvoiceRoute(route)) return true;
  if (typeof window !== 'undefined' && PROVISION_INVOICE_PATH_RE.test(window.location.pathname)) {
    return true;
  }
  return false;
};

const serializeRoute = (route) => ({
  name: route.name,
  params: route.params || {},
  query: route.query || {},
});

const readStash = () => {
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clearStash = () => {
  sessionStorage.removeItem(STASH_KEY);
};

const readLocationCheckoutQuery = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const checkout = params.get('checkout');
  if (!checkout) return null;
  const sessionId = params.get('session_id');
  return {
    checkout,
    ...(sessionId ? { session_id: sessionId } : {}),
  };
};

export const readPartnerCheckoutLocationQuery = readLocationCheckoutQuery;

export const rememberPartnerReturnRoute = (route) => {
  if (!route?.name || PARTNER_CHECKOUT_ROUTE_NAMES.has(route.name)) return;
  sessionStorage.setItem(RETURN_ROUTE_KEY, JSON.stringify(serializeRoute(route)));
};

export const ensurePartnerReturnRouteBeforeCheckout = (fallback = DEFAULT_PARTNER_RETURN) => {
  if (!sessionStorage.getItem(RETURN_ROUTE_KEY)) {
    sessionStorage.setItem(RETURN_ROUTE_KEY, JSON.stringify(fallback));
  }
};

export const stashPartnerCheckoutReturn = (route, { fallback = DEFAULT_HIGHLIGHT_CHECKOUT_RETURN } = {}) => {
  if (route?.name && !PARTNER_CHECKOUT_ROUTE_NAMES.has(route.name)) {
    rememberPartnerReturnRoute(route);
  } else {
    ensurePartnerReturnRouteBeforeCheckout(fallback);
  }
  const returnRoute = getPartnerReturnRoute();
  sessionStorage.setItem(STASH_KEY, JSON.stringify({
    routeName: returnRoute.name,
    params: returnRoute.params || {},
    query: {},
  }));
};

export const markPartnerStripeCheckoutStarted = (pollKind = '') => {
  sessionStorage.setItem(STRIPE_CHECKOUT_KEY, '1');
  if (pollKind) {
    sessionStorage.setItem(STRIPE_CHECKOUT_KIND_KEY, pollKind);
  }
};

export const peekPartnerStripeCheckoutKind = () => (
  sessionStorage.getItem(STRIPE_CHECKOUT_KIND_KEY) || ''
);

export const consumePartnerStripeCheckoutKind = () => {
  const kind = peekPartnerStripeCheckoutKind();
  sessionStorage.removeItem(STRIPE_CHECKOUT_KIND_KEY);
  return kind;
};

export const peekPartnerStripeCheckoutStarted = () => (
  sessionStorage.getItem(STRIPE_CHECKOUT_KEY) === '1'
);

/**
 * Open Stripe Checkout without dropping the Partner page from history.
 *
 * iOS presents checkout.stripe.com in SFSafariViewController ("Fertig").
 * `location.replace` removes the app entry, so Fertig reveals a blank Ionic
 * page. Stripe's own back control still works because it loads cancel_url.
 * Member native/PWA keeps the WebView on-origin, which is why Fertig is fine there.
 */
export const openPartnerStripeCheckout = (url) => {
  if (!url) return;
  window.location.assign(url);
};

/**
 * After iOS "Fertig", the SPA can come back from bfcache with every ion-page hidden
 * (white screen) and without cancel_url. Restore the UI; leave success/cancel query
 * handling to handlePartnerCheckoutReturn.
 */
export const isPartnerMembershipSubscriptionCheckoutReturn = (router) => {
  const query = getPartnerCheckoutQuery(router);
  if (!query?.checkout) return false;
  if (isCommissionInvoiceCheckoutReturn(router)) return false;
  if (typeof window !== 'undefined' && window.location.pathname.includes('/navigation/membership')) {
    return true;
  }
  if (isPartnerMembershipCheckoutStarted()) return true;
  if (peekPendingPartnerMembershipModal()) return true;
  const returnRoute = getPartnerReturnRoute();
  return returnRoute.name === PARTNER_PROFILE_ROUTE.name
    || returnRoute.name === 'navigation.membership';
};

export const deferPartnerMembershipCheckoutToModal = async (router) => {
  const query = getPartnerCheckoutQuery(router);
  if (!query?.checkout) return false;

  syncPartnerMembershipCheckoutFromLocation(readLocationCheckoutQuery);
  stashPendingPartnerMembershipModal({
    checkout: query.checkout,
    session_id: query.session_id,
  });

  if (router.currentRoute.value.name !== PARTNER_PROFILE_ROUTE.name) {
    await router.replace(PARTNER_PROFILE_ROUTE);
  }

  const { openPendingPartnerMembershipModalIfAny } = await import('@/utils/openPartnerMembershipModal');
  await openPendingPartnerMembershipModalIfAny();
  return true;
};

export const installPartnerStripeCheckoutResume = () => {
  installIosPwaLayoutRepair({
    stripeCheckoutSessionKey: STRIPE_CHECKOUT_KEY,
    readLocationCheckoutQuery: readLocationCheckoutQuery,
    onBfCacheWithoutCheckout: () => {
      markPartnerMembershipModalReopen();
      void import('@/utils/openPartnerMembershipModal').then((module) => {
        module.openPendingPartnerMembershipModalIfAny();
      });
      void import('@/utils/reconcileAbandonedPartnerCheckout').then((module) => {
        void module.reconcileAbandonedPartnerCheckout();
      });
    },
    onStripeMembershipReturn: () => {
      if (!syncPartnerMembershipCheckoutFromLocation(readLocationCheckoutQuery)) return false;
      void import('@/utils/openPartnerMembershipModal').then((module) => {
        module.openPendingPartnerMembershipModalIfAny();
      });
      return true;
    },
  });
};

export const consumePartnerStripeCheckoutStarted = () => {
  const started = sessionStorage.getItem(STRIPE_CHECKOUT_KEY) === '1';
  sessionStorage.removeItem(STRIPE_CHECKOUT_KEY);
  return started;
};

export const getPartnerReturnRoute = () => {
  const raw = sessionStorage.getItem(RETURN_ROUTE_KEY);
  if (!raw) return { ...DEFAULT_PARTNER_RETURN };

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.name) return parsed;
  } catch {
    // ignore invalid storage
  }

  return { ...DEFAULT_PARTNER_RETURN };
};

export const clearPartnerReturnRoute = () => {
  sessionStorage.removeItem(RETURN_ROUTE_KEY);
};

export const hasStoredPartnerReturnRoute = () => (
  Boolean(sessionStorage.getItem(RETURN_ROUTE_KEY))
);

export const removePartnerBackGuard = () => {
  if (backGuardTeardown) {
    backGuardTeardown();
    backGuardTeardown = null;
  }
};

export const installPartnerBackGuard = (router) => {
  removePartnerBackGuard();

  const onPopState = () => {
    removePartnerBackGuard();
    navigatePartnerBack(router);
  };

  window.history.pushState({ partnerBackGuard: true }, '');
  window.addEventListener('popstate', onPopState);

  backGuardTeardown = () => {
    window.removeEventListener('popstate', onPopState);
  };
};

export const setupPartnerBackGuardAfterStripeReturn = (router) => {
  if (!consumePartnerStripeCheckoutStarted()) return;
  installPartnerBackGuard(router);
};

export const removePartnerMembershipModalBackGuard = () => {
  if (membershipModalBackGuardTeardown) {
    membershipModalBackGuardTeardown();
    membershipModalBackGuardTeardown = null;
  }
};

/**
 * Membership checkout in a fullscreen modal: hardware back should dismiss overlays,
 * not navigate to cockpit via navigatePartnerBack.
 */
export const installPartnerMembershipModalBackGuard = () => {
  removePartnerMembershipModalBackGuard();

  const onPopState = () => {
    removePartnerMembershipModalBackGuard();
    void modalController.getTop().then((top) => {
      if (top) void top.dismiss();
    });
  };

  window.history.pushState({ partnerMembershipBackGuard: true }, '');
  window.addEventListener('popstate', onPopState);

  membershipModalBackGuardTeardown = () => {
    window.removeEventListener('popstate', onPopState);
  };
};

export const setupPartnerMembershipModalBackGuardAfterStripeReturn = () => {
  if (!consumePartnerStripeCheckoutStarted()) return;
  consumePartnerMembershipCheckoutStarted();
  installPartnerMembershipModalBackGuard();
};

export const shouldPreservePartnerSessionRoute = (router, store) => {
  const checkoutQuery = getPartnerCheckoutQuery(router);
  const route = router.currentRoute?.value;
  if (checkoutQuery.checkout) return true;
  if (route?.name === 'navigation.checkout-return') return true;
  if (readStripeEmbeddedCheckoutContext()) return true;
  if (isPartnerCheckoutReturnRoute(route, router)) return true;
  if (isPartnerMembershipCheckoutStarted()) return true;
  if (peekPendingPartnerMembershipModal()) return true;
  if (store?.getters?.['ui/isOverlayOpen']) return true;
  return false;
};

export const handlePartnerCheckoutResumeInAuthFlow = async (router) => {
  if (router.currentRoute?.value?.name === 'navigation.checkout-return') {
    return true;
  }
  if (isPartnerMembershipSubscriptionCheckoutReturn(router)) {
    await deferPartnerMembershipCheckoutToModal(router);
    return true;
  }
  await restorePartnerCheckoutReturn(router);
  return true;
};

/**
 * Payment result pages must not use browser history.back() — external Stripe
 * entries would trap the user. Always navigate to the stored in-app route.
 */
export const navigatePartnerBack = (router) => {
  removePartnerBackGuard();
  const target = getPartnerReturnRoute();
  clearPartnerReturnRoute();
  router.replace(target);
};

export const getPartnerCheckoutQuery = (router) => {
  const route = router?.currentRoute?.value;
  if (route?.query?.checkout) {
    return { ...route.query };
  }

  const fromLocation = readLocationCheckoutQuery();
  if (fromLocation) return fromLocation;

  const stash = readStash();
  if (stash?.query?.checkout) return { ...stash.query };

  return {};
};

export const isPartnerCheckoutReturnRoute = (route, router) => {
  const query = route?.query?.checkout
    ? route.query
    : getPartnerCheckoutQuery(router || { currentRoute: { value: route } });
  if (!query?.checkout) return false;

  if (isProvisionInvoiceRoute(route)) return true;

  if (PARTNER_CHECKOUT_ROUTE_NAMES.has(route?.name)) return true;

  const path = String(route?.path || route?.fullPath || '');
  if (PARTNER_CHECKOUT_PATH_RE.test(path)) return true;

  if (typeof window !== 'undefined') {
    if (PROVISION_INVOICE_PATH_RE.test(window.location.pathname)) return true;
    return PARTNER_CHECKOUT_PATH_RE.test(window.location.pathname);
  }

  return false;
};

export const hasPendingPartnerCheckout = (routerOrRoute) => {
  const router = routerOrRoute?.currentRoute
    ? routerOrRoute
    : { currentRoute: { value: routerOrRoute } };
  return Boolean(getPartnerCheckoutQuery(router).checkout);
};

export const stashPartnerInvoiceCheckoutReturn = (route) => {
  const invoiceRoute = serializeRoute(route || { name: 'navigation.provision-invoice' });
  sessionStorage.setItem(RETURN_ROUTE_KEY, JSON.stringify({
    name: 'navigation.provision',
    params: {},
    query: {},
  }));
  sessionStorage.setItem(STASH_KEY, JSON.stringify({
    routeName: invoiceRoute.name,
    params: invoiceRoute.params,
    checkoutType: COMMISSION_INVOICE_CHECKOUT_TYPE,
    query: {},
  }));
};

export const restorePartnerCheckoutReturn = async (router) => {
  const query = getPartnerCheckoutQuery(router);
  if (!query.checkout) return false;

  if (isCommissionInvoiceCheckoutReturn(router)) {
    const current = router.currentRoute.value;
    const stash = readStash();
    const targetName = isProvisionInvoiceRoute(current)
      ? current.name
      : (stash?.routeName || 'navigation.provision-invoice');
    const targetParams = isProvisionInvoiceRoute(current)
      ? (current.params || {})
      : (stash?.params || {});

    if (
      current.name === targetName
      && current.query?.checkout === query.checkout
      && JSON.stringify(current.params || {}) === JSON.stringify(targetParams)
    ) {
      clearStash();
      return true;
    }

    await router.replace({
      name: targetName,
      params: targetParams,
      query: {
        checkout: query.checkout,
        ...(query.session_id ? { session_id: query.session_id } : {}),
      },
    });
    clearStash();
    return true;
  }

  const current = router.currentRoute.value;
  const returnRoute = getPartnerReturnRoute();
  const onReturnRoute = current.name === returnRoute.name
    || PARTNER_CHECKOUT_PATH_RE.test(String(current.path || ''));

  if (onReturnRoute && current.query?.checkout === query.checkout) {
    clearStash();
    return true;
  }

  await router.replace({
    name: returnRoute.name,
    params: returnRoute.params || {},
    query: {
      checkout: query.checkout,
      ...(query.session_id ? { session_id: query.session_id } : {}),
    },
  });
  clearStash();
  return true;
};

const handledStorageKey = (checkout, sessionId) => (
  `${HANDLED_KEY_PREFIX}${sessionId || `checkout:${checkout}`}`
);

const isAlreadyHandled = (checkout, sessionId) => (
  sessionStorage.getItem(handledStorageKey(checkout, sessionId)) === '1'
);

const markHandled = (checkout, sessionId) => {
  sessionStorage.setItem(handledStorageKey(checkout, sessionId), '1');
};

const clearHandled = (checkout, sessionId) => {
  sessionStorage.removeItem(handledStorageKey(checkout, sessionId));
};

const checkoutLockKey = (checkout, sessionId) => sessionId || `checkout:${checkout}`;

const clearCheckoutQuery = async (router) => {
  const current = router.currentRoute.value;
  if (!current.query?.checkout && !current.query?.session_id) return;
  await router.replace({
    name: current.name || 'navigation.profile',
    params: current.params || {},
    query: {},
  });
};

/**
 * Membership subscription checkout return handled inside the fullscreen membership modal.
 */
export const processPartnerMembershipCheckoutProps = async (router, { checkout, sessionId }) => {
  if (!checkout) return false;

  if (isAlreadyHandled(checkout, sessionId)) return true;

  if (checkout === 'cancel') {
    markHandled(checkout, sessionId);
    setupPartnerMembershipModalBackGuardAfterStripeReturn();
    await showPartnerCheckoutModal('error', {
      title: 'Kauf abgebrochen',
      message: 'Du kannst jederzeit eine andere Zahlungsmethode wählen.',
      buttonLabel: 'OK',
    });
    return true;
  }

  if (checkout !== 'success' || !sessionId) {
    markHandled(checkout, sessionId);
    return true;
  }

  markHandled(checkout, sessionId);

  const result = await pollPartnerCheckoutReturn(sessionId);
  if (result?.ok) {
    setupPartnerMembershipModalBackGuardAfterStripeReturn();
  } else {
    clearHandled(checkout, sessionId);
  }
  return true;
};

const processPartnerCheckoutReturn = async (router) => {
  if (isCommissionInvoiceCheckoutReturn(router)) {
    await restorePartnerCheckoutReturn(router);
    return true;
  }

  await restorePartnerCheckoutReturn(router);

  const route = router.currentRoute.value;
  const checkout = route.query?.checkout || getPartnerCheckoutQuery(router).checkout;
  const sessionId = route.query?.session_id || getPartnerCheckoutQuery(router).session_id;
  if (!checkout) return false;

  if (isAlreadyHandled(checkout, sessionId)) {
    await clearCheckoutQuery(router);
    return true;
  }

  if (checkout === 'cancel') {
    markHandled(checkout, sessionId);
    await clearCheckoutQuery(router);
    setupPartnerBackGuardAfterStripeReturn(router);
    try {
      await cancelHighlightCheckout(sessionId || undefined);
      await store.dispatch('partner/refreshStatus');
      notifyPartnerStatusChanged({ status: store.getters['partner/getStatus'] });
    } catch (err) {
      console.warn('Partner highlight checkout cancel cleanup failed', err);
    }
    await showPartnerCheckoutModal('error', {
      title: 'Kauf abgebrochen',
      message: 'Du kannst jederzeit eine andere Zahlungsmethode wählen.',
      buttonLabel: 'OK',
    });
    return true;
  }

  if (checkout !== 'success' || !sessionId) {
    await clearCheckoutQuery(router);
    return true;
  }

  markHandled(checkout, sessionId);

  const result = await pollPartnerCheckoutReturn(sessionId);
  if (result?.ok) {
    await clearCheckoutQuery(router);
    setupPartnerBackGuardAfterStripeReturn(router);
  } else {
    clearHandled(checkout, sessionId);
  }
  return true;
};

/**
 * Process Stripe return query params once per session_id.
 * Returns true when checkout query was consumed (success, cancel, or duplicate).
 */
export const handlePartnerCheckoutReturn = async (router) => {
  const route = router.currentRoute.value;
  const checkout = route.query?.checkout || getPartnerCheckoutQuery(router).checkout;
  const sessionId = route.query?.session_id || getPartnerCheckoutQuery(router).session_id;
  if (!checkout) return false;

  if (isCommissionInvoiceCheckoutReturn(router)) {
    const lockKey = checkoutLockKey(checkout, sessionId);
    const inFlight = inFlightByKey.get(lockKey);
    if (inFlight) return inFlight;

    const task = restorePartnerCheckoutReturn(router);
    inFlightByKey.set(lockKey, task);
    task.finally(() => {
      if (inFlightByKey.get(lockKey) === task) {
        inFlightByKey.delete(lockKey);
      }
    });
    return task;
  }

  if (isPartnerMembershipSubscriptionCheckoutReturn(router)) {
    const lockKey = checkoutLockKey(checkout, sessionId);
    const inFlight = inFlightByKey.get(lockKey);
    if (inFlight) return inFlight;

    const task = deferPartnerMembershipCheckoutToModal(router);
    inFlightByKey.set(lockKey, task);
    task.finally(() => {
      if (inFlightByKey.get(lockKey) === task) {
        inFlightByKey.delete(lockKey);
      }
    });
    return task;
  }

  const lockKey = checkoutLockKey(checkout, sessionId);
  const inFlight = inFlightByKey.get(lockKey);
  if (inFlight) {
    return inFlight;
  }

  const task = processPartnerCheckoutReturn(router);
  inFlightByKey.set(lockKey, task);
  task.finally(() => {
    if (inFlightByKey.get(lockKey) === task) {
      inFlightByKey.delete(lockKey);
    }
  });
  return task;
};
